import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import { formatCurrency } from '../utils/currency';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

export default function StockTransferList() {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sourceStock, setSourceStock] = useState([]);
  const [sourceStockLoading, setSourceStockLoading] = useState(false);
  const [fromProjectSearch, setFromProjectSearch] = useState('');
  const [fromProjectDropdownOpen, setFromProjectDropdownOpen] = useState(false);
  const [toProjectSearch, setToProjectSearch] = useState('');
  const [toProjectDropdownOpen, setToProjectDropdownOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: '',
    id: null,
    title: '',
    message: '',
    confirmText: '',
    confirmClass: ''
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);

  // Form states
  const [showFormModal, setShowFormModal] = useState(false);
  const [formData, setFormData] = useState({
    fromType: 'WAREHOUSE', // WAREHOUSE or PROJECT
    fromWarehouseId: '',
    fromProjectId: '',
    toType: 'WAREHOUSE', // WAREHOUSE or PROJECT
    toWarehouseId: '',
    toProjectId: '',
    notes: ''
  });

  const [items, setItems] = useState([
    { inventoryItemId: '', quantity: '', notes: '' }
  ]);

  useEffect(() => {
    fetchTransfers();
    fetchActiveWarehouses();
    fetchProjects();
  }, []);



  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/stock-transfers');
      const data = res.data.data || res.data || [];
      const sorted = [...data].sort((a, b) => b.id - a.id);
      setTransfers(sorted);
      setCurrentPage(0);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load stock transfers');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveWarehouses = async () => {
    try {
      const res = await axios.get('/warehouses?activeOnly=true');
      const data = res.data.data || res.data || [];
      setWarehouses(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get('/projects?size=1000');
      const data = res.data.data?.content || res.data.content || res.data.data || [];
      setProjects(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Error fetching projects:', e);
    }
  };

  const fetchSourceStock = async (type, id) => {
    if (!id) {
      setSourceStock([]);
      return;
    }
    setSourceStockLoading(true);
    try {
      const url = type === 'PROJECT' ? `/projects/${id}/stock` : `/warehouses/${id}/stock`;
      const res = await axios.get(url);
      const data = res.data.data || res.data || [];
      setSourceStock(data);
    } catch (e) {
      console.error(e);
      setSourceStock([]);
    } finally {
      setSourceStockLoading(false);
    }
  };

  const handleSourceTypeChange = (e) => {
    const val = e.target.value;
    setFormData(prev => ({
      ...prev,
      fromType: val,
      fromWarehouseId: '',
      fromProjectId: ''
    }));
    setItems([{ inventoryItemId: '', quantity: '', notes: '' }]);
    setSourceStock([]);
  };

  const handleSourceIdChange = (eOrVal) => {
    const val = (eOrVal && typeof eOrVal === 'object' && eOrVal.target) ? eOrVal.target.value : eOrVal;
    setFormData(prev => ({
      ...prev,
      fromWarehouseId: formData.fromType === 'WAREHOUSE' ? val : '',
      fromProjectId: formData.fromType === 'PROJECT' ? val : ''
    }));
    setItems([{ inventoryItemId: '', quantity: '', notes: '' }]);
    fetchSourceStock(formData.fromType, val);
  };

  const handleAddRow = () => {
    setItems(prev => [...prev, { inventoryItemId: '', quantity: '', unitPrice: '', notes: '' }]);
  };

  const handleRemoveRow = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleRowItemChange = (index, itemId) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        const selectedStockItem = sourceStock.find(s => String(s.inventoryItemId) === String(itemId));
        const price = selectedStockItem ? (selectedStockItem.purchasePrice || 0) : '';
        return { ...row, inventoryItemId: itemId, quantity: '', unitPrice: price, dropdownOpen: false, searchQuery: '' };
      }
      return row;
    }));
  };

  const handleRowQuantityChange = (index, quantity) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        return { ...row, quantity };
      }
      return row;
    }));
  };

  const handleRowUnitPriceChange = (index, unitPrice) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        return { ...row, unitPrice };
      }
      return row;
    }));
  };

  const handleRowNotesChange = (index, notes) => {
    setItems(prev => prev.map((row, i) => {
      if (i === index) {
        return { ...row, notes };
      }
      return row;
    }));
  };

  const handleOpenCreate = () => {
    setFormData({
      fromType: 'WAREHOUSE',
      fromWarehouseId: '',
      fromProjectId: '',
      toType: 'WAREHOUSE',
      toWarehouseId: '',
      toProjectId: '',
      notes: ''
    });
    setItems([{ inventoryItemId: '', quantity: '', unitPrice: '', notes: '' }]);
    setSourceStock([]);
    setShowFormModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { fromType, fromWarehouseId, fromProjectId, toType, toWarehouseId, toProjectId } = formData;
    
    const sourceId = fromType === 'PROJECT' ? fromProjectId : fromWarehouseId;
    const targetId = toType === 'PROJECT' ? toProjectId : toWarehouseId;

    if (!sourceId || !targetId) {
      toast.error('Source and Target locations are required.');
      return;
    }

    if (fromType === toType && String(sourceId) === String(targetId)) {
      toast.error('Source and Target cannot be the same location.');
      return;
    }

    if (items.some(x => !x.inventoryItemId || !x.quantity)) {
      toast.error('Please select an item and fill in transfer quantities for all rows.');
      return;
    }

    // Validate quantities (including duplicate items in multiple rows)
    const quantityByItem = {};
    for (const item of items) {
      const id = item.inventoryItemId;
      const qty = parseFloat(item.quantity);
      quantityByItem[id] = (quantityByItem[id] || 0) + qty;
    }
    
    for (const [itemId, qty] of Object.entries(quantityByItem)) {
      const selectedItem = sourceStock.find(s => String(s.inventoryItemId) === String(itemId));
      if (!selectedItem || selectedItem.quantity < qty) {
        toast.error(`Insufficient stock in source for ${selectedItem ? selectedItem.itemName : 'selected item'}. Available: ${selectedItem ? selectedItem.quantity : 0}, Requested: ${qty}`);
        return;
      }
    }

    try {
      const generatedGroupId = `TRF-B-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`;
      const payload = items.map(item => {
        const selectedStockItem = sourceStock.find(x => String(x.inventoryItemId) === String(item.inventoryItemId));
        const price = item.unitPrice !== undefined && item.unitPrice !== '' ? parseFloat(item.unitPrice) : (selectedStockItem ? selectedStockItem.purchasePrice || 0 : 0);
        return {
          fromWarehouseId: fromType === 'WAREHOUSE' ? parseInt(fromWarehouseId) : null,
          fromProjectId: fromType === 'PROJECT' ? parseInt(fromProjectId) : null,
          toWarehouseId: toType === 'WAREHOUSE' ? parseInt(toWarehouseId) : null,
          toProjectId: toType === 'PROJECT' ? parseInt(toProjectId) : null,
          inventoryItemId: parseInt(item.inventoryItemId),
          quantity: parseFloat(item.quantity),
          unitPrice: price,
          notes: item.notes || formData.notes || null,
          groupId: generatedGroupId
        };
      });

      await axios.post('/stock-transfers/batch', payload);
      toast.success('Batch stock transfer initiated successfully');
      setShowFormModal(false);
      fetchTransfers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to initiate transfer');
    }
  };

  const handleComplete = (id) => {
    setConfirmModal({
      isOpen: true,
      type: 'complete',
      id,
      title: 'Complete Stock Transfer',
      message: 'Are you sure you want to complete this transfer? This will adjust stock levels and post project financial cost entries.',
      confirmText: 'Complete',
      confirmClass: 'btn-success'
    });
  };

  const handleCancel = (id) => {
    setConfirmModal({
      isOpen: true,
      type: 'cancel',
      id,
      title: 'Cancel Stock Transfer',
      message: 'Are you sure you want to cancel this transfer?',
      confirmText: 'Cancel Transfer',
      confirmClass: 'btn-danger'
    });
  };

  const handleConfirmAction = async () => {
    const { type, id } = confirmModal;
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
    if (!id) return;
    try {
      if (type === 'complete') {
        await axios.post(`/stock-transfers/${id}/complete`);
        toast.success('Stock transfer completed and cost entries allocated successfully.');
      } else if (type === 'cancel') {
        await axios.post(`/stock-transfers/${id}/cancel`);
        toast.success('Transfer cancelled.');
      }
      fetchTransfers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || `Failed to ${type} transfer`);
    }
  };

  const groupTransfers = (list) => {
    const groups = {};
    const result = [];
    list.forEach(t => {
      if (t.groupId && t.groupId.trim() !== '') {
        if (!groups[t.groupId]) {
          groups[t.groupId] = {
            id: t.id,
            groupId: t.groupId,
            voucherNo: t.groupId,
            transferDate: t.transferDate,
            fromWarehouseName: t.fromWarehouseName,
            fromWarehouseCode: t.fromWarehouseCode,
            fromProjectName: t.fromProjectName,
            toWarehouseName: t.toWarehouseName,
            toWarehouseCode: t.toWarehouseCode,
            toProjectName: t.toProjectName,
            status: t.status,
            notes: t.notes,
            items: []
          };
          result.push(groups[t.groupId]);
        }
        groups[t.groupId].items.push(t);
      } else {
        result.push({
          id: t.id,
          groupId: null,
          voucherNo: t.voucherNo,
          transferDate: t.transferDate,
          fromWarehouseName: t.fromWarehouseName,
          fromWarehouseCode: t.fromWarehouseCode,
          fromProjectName: t.fromProjectName,
          toWarehouseName: t.toWarehouseName,
          toWarehouseCode: t.toWarehouseCode,
          toProjectName: t.toProjectName,
          status: t.status,
          notes: t.notes,
          items: [t]
        });
      }
    });
    return result;
  };

  const groupedList = groupTransfers(transfers);
  const startIndex = currentPage * itemsPerPage;
  const paginatedTransfers = groupedList.slice(startIndex, startIndex + itemsPerPage);
  const totalPages = Math.ceil(groupedList.length / itemsPerPage);

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Stock Transfers</h1>
        <button className="btn-primary" onClick={handleOpenCreate}>New Stock Transfer</button>
      </div>



      <div className="card">
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Loading transfers...</div>
        ) : transfers.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No stock transfers found.</div>
        ) : (
          <div className="table-responsive">
            <table className="table" style={{ fontSize: '13px' }}>
              <thead>
                <tr>
                  <th>Voucher No</th>
                  <th>Date</th>
                  <th>Source (From)</th>
                  <th>Destination (To)</th>
                  <th>Item</th>
                  <th style={{ textAlign: 'right' }}>Quantity</th>
                  <th style={{ textAlign: 'right' }}>Unit Cost</th>
                  <th style={{ textAlign: 'right' }}>Total Value</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransfers.map(t => (
                  <tr key={t.groupId || t.id}>
                    <td><strong>{t.voucherNo}</strong></td>
                    <td>{new Date(t.transferDate).toLocaleDateString()}</td>
                    <td>
                      {t.fromWarehouseName ? (
                        <span>🏢 {t.fromWarehouseName} ({t.fromWarehouseCode})</span>
                      ) : (
                        <span>🚧 {t.fromProjectName}</span>
                      )}
                    </td>
                    <td>
                      {t.toWarehouseName ? (
                        <span>🏢 {t.toWarehouseName} ({t.toWarehouseCode})</span>
                      ) : (
                        <span>🚧 {t.toProjectName}</span>
                      )}
                    </td>
                    <td>
                      {t.items.length === 1 ? (
                        <>
                          <div><strong>{t.items[0].inventoryItemName}</strong></div>
                          <div style={{ fontSize: '11px', color: '#666' }}>{t.items[0].inventoryItemCode}</div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontWeight: '600', color: '#2563eb' }}>📦 Multi-Item Transfer</div>
                          <div style={{ fontSize: '11.5px', color: '#4b5563', whiteSpace: 'normal', maxWidth: '250px' }}>
                            {t.items.map(x => `${x.inventoryItemName} (x${x.quantity})`).join(', ')}
                          </div>
                        </>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                      {t.items.length === 1 ? (
                        `${t.items[0].quantity} ${t.items[0].unitOfMeasurement}`
                      ) : (
                        `${t.items.reduce((sum, x) => sum + x.quantity, 0)} units`
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {t.items.length === 1 ? formatCurrency(t.items[0].unitPrice) : '-'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#10b981' }}>
                      {formatCurrency(t.items.reduce((sum, x) => sum + x.amount, 0))}
                    </td>
                    <td>
                      <span className={`badge badge-${t.status === 'COMPLETED' ? 'success' : t.status === 'PENDING' ? 'warning' : 'danger'}`}>
                        {t.status}
                      </span>
                    </td>
                    <td>
                      {t.status === 'PENDING' && (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button className="btn-success btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }} onClick={() => handleComplete(t.id)}>Complete</button>
                          <button className="btn-danger btn-sm" style={{ padding: '2px 8px', fontSize: '11px' }} onClick={() => handleCancel(t.id)}>Cancel</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px', padding: '10px 15px', borderTop: '1px solid var(--border, #e5e7eb)' }}>
                <button 
                  className="btn btn-secondary btn-sm" 
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  Previous
                </button>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary, #666)' }}>
                  Page {currentPage + 1} of {totalPages}
                </span>
                <button 
                  className="btn btn-secondary btn-sm" 
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Stock Transfer Modal */}
      {showFormModal && (
        <div className="modal-overlay" style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', zIndex: 1000, overflowY: 'auto', padding: '20px' }} onClick={() => setShowFormModal(false)}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '1000px', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>New Stock Transfer</h2>
              <button className="close-button" style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }} onClick={() => setShowFormModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div className="modal-body" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '15px', overflowY: 'auto' }}>
                
                {/* Source & Target Configuration Row */}
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {/* SOURCE CONFIG */}
                  <div style={{ flex: 1, minWidth: '280px', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '15px', backgroundColor: '#f9fafb' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>Source (From)</h3>
                    <div className="form-group" style={{ marginBottom: '10px' }}>
                      <label>Source Type *</label>
                      <select value={formData.fromType} onChange={handleSourceTypeChange} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}>
                        <option value="WAREHOUSE">Warehouse</option>
                        <option value="PROJECT">Project Site</option>
                      </select>
                    </div>

                    {formData.fromType === 'WAREHOUSE' ? (
                      <div className="form-group">
                        <label>Source Warehouse *</label>
                        <select 
                          value={formData.fromWarehouseId} 
                          onChange={handleSourceIdChange}
                          required
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                          <option value="">Select Warehouse</option>
                          {warehouses.map(w => (
                            <option key={w.id} value={w.id}>{w.name} ({w.code})</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="form-group" style={{ position: 'relative' }}>
                        <label>Source Project *</label>
                        <input
                          type="text"
                          placeholder="Type to search project..."
                          value={fromProjectDropdownOpen ? fromProjectSearch : (projects.find(p => String(p.id) === String(formData.fromProjectId)) ? `${projects.find(p => String(p.id) === String(formData.fromProjectId)).name} (${projects.find(p => String(p.id) === String(formData.fromProjectId)).customerName})` : '')}
                          onFocus={() => {
                            setFromProjectSearch('');
                            setFromProjectDropdownOpen(true);
                          }}
                          onChange={(e) => setFromProjectSearch(e.target.value)}
                          onBlur={() => setTimeout(() => setFromProjectDropdownOpen(false), 200)}
                          required
                          style={{ cursor: 'pointer' }}
                        />
                        {fromProjectDropdownOpen && (
                          <div className="custom-select-dropdown" style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 10,
                            background: 'var(--bg-card, #ffffff)',
                            border: '1px solid var(--border, #ddd)',
                            borderRadius: '6px',
                            boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            marginTop: '4px'
                          }}>
                            {projects
                              .filter(p => {
                                const q = fromProjectSearch.toLowerCase();
                                return p.name.toLowerCase().includes(q) || (p.customerName || '').toLowerCase().includes(q);
                              })
                              .map(p => (
                                <div
                                  key={p.id}
                                  className="custom-select-option"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleSourceIdChange(String(p.id));
                                    setFromProjectDropdownOpen(false);
                                    setFromProjectSearch('');
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    borderBottom: '1px solid var(--border, #f0f0f0)',
                                    backgroundColor: String(p.id) === String(formData.fromProjectId) ? 'var(--menu-active-bg, #e8f4ff)' : 'transparent',
                                    color: String(p.id) === String(formData.fromProjectId) ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (String(p.id) !== String(formData.fromProjectId)) e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (String(p.id) !== String(formData.fromProjectId)) e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {p.name} ({p.customerName})
                                </div>
                              ))
                            }
                            {projects.filter(p => {
                              const q = fromProjectSearch.toLowerCase();
                              return p.name.toLowerCase().includes(q) || (p.customerName || '').toLowerCase().includes(q);
                            }).length === 0 && (
                              <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                                No projects found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* TARGET CONFIG */}
                  <div style={{ flex: 1, minWidth: '280px', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '15px', backgroundColor: '#f9fafb' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: '600' }}>Target (To)</h3>
                    <div className="form-group" style={{ marginBottom: '10px' }}>
                      <label>Target Type *</label>
                      <select value={formData.toType} onChange={e => setFormData(prev => ({ ...prev, toType: e.target.value, toWarehouseId: '', toProjectId: '' }))} style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}>
                        <option value="WAREHOUSE">Warehouse</option>
                        <option value="PROJECT">Project Site</option>
                      </select>
                    </div>

                    {formData.toType === 'WAREHOUSE' ? (
                      <div className="form-group">
                        <label>Target Warehouse *</label>
                        <select 
                          value={formData.toWarehouseId} 
                          onChange={e => setFormData(prev => ({ ...prev, toWarehouseId: e.target.value }))}
                          required
                          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                          <option value="">Select Warehouse</option>
                          {warehouses
                            .filter(w => formData.fromType !== 'WAREHOUSE' || String(w.id) !== String(formData.fromWarehouseId))
                            .map(w => (
                              <option key={w.id} value={w.id}>{w.name} ({w.code})</option>
                            ))}
                        </select>
                      </div>
                    ) : (
                      <div className="form-group" style={{ position: 'relative' }}>
                        <label>Target Project *</label>
                        <input
                          type="text"
                          placeholder="Type to search project..."
                          value={toProjectDropdownOpen ? toProjectSearch : (projects.find(p => String(p.id) === String(formData.toProjectId)) ? `${projects.find(p => String(p.id) === String(formData.toProjectId)).name} (${projects.find(p => String(p.id) === String(formData.toProjectId)).customerName})` : '')}
                          onFocus={() => {
                            setToProjectSearch('');
                            setToProjectDropdownOpen(true);
                          }}
                          onChange={(e) => setToProjectSearch(e.target.value)}
                          onBlur={() => setTimeout(() => setToProjectDropdownOpen(false), 200)}
                          required
                          style={{ cursor: 'pointer' }}
                        />
                        {toProjectDropdownOpen && (
                          <div className="custom-select-dropdown" style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 10,
                            background: 'var(--bg-card, #ffffff)',
                            border: '1px solid var(--border, #ddd)',
                            borderRadius: '6px',
                            boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            marginTop: '4px'
                          }}>
                            {projects
                              .filter(p => formData.fromType !== 'PROJECT' || String(p.id) !== String(formData.fromProjectId))
                              .filter(p => {
                                const q = toProjectSearch.toLowerCase();
                                return p.name.toLowerCase().includes(q) || (p.customerName || '').toLowerCase().includes(q);
                              })
                              .map(p => (
                                <div
                                  key={p.id}
                                  className="custom-select-option"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    setFormData(prev => ({ ...prev, toProjectId: String(p.id) }));
                                    setToProjectDropdownOpen(false);
                                    setToProjectSearch('');
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    borderBottom: '1px solid var(--border, #f0f0f0)',
                                    backgroundColor: String(p.id) === String(formData.toProjectId) ? 'var(--menu-active-bg, #e8f4ff)' : 'transparent',
                                    color: String(p.id) === String(formData.toProjectId) ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (String(p.id) !== String(formData.toProjectId)) e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                                  }}
                                  onMouseLeave={(e) => {
                                    if (String(p.id) !== String(formData.toProjectId)) e.target.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  {p.name} ({p.customerName})
                                </div>
                              ))
                            }
                            {projects
                              .filter(p => formData.fromType !== 'PROJECT' || String(p.id) !== String(formData.fromProjectId))
                              .filter(p => {
                                const q = toProjectSearch.toLowerCase();
                                return p.name.toLowerCase().includes(q) || (p.customerName || '').toLowerCase().includes(q);
                              }).length === 0 && (
                              <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                                No projects found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Items List Row Configuration */}
                <div style={{ marginTop: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Transfer Items List</span>
                    <button
                      type="button"
                      onClick={handleAddRow}
                      disabled={!(formData.fromType === 'PROJECT' ? formData.fromProjectId : formData.fromWarehouseId)}
                      className="btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      + Add Item
                    </button>
                  </div>

                  {items.map((row, index) => {
                    const selectedStockItem = sourceStock.find(x => String(x.inventoryItemId) === String(row.inventoryItemId));
                    const unitPriceVal = row.unitPrice !== undefined && row.unitPrice !== '' ? row.unitPrice : (selectedStockItem ? selectedStockItem.purchasePrice || 0 : 0);
                    const totalAmount = row.quantity && unitPriceVal ? parseFloat(row.quantity) * parseFloat(unitPriceVal) : 0;
                    return (
                      <div key={index} style={{ 
                        display: 'flex', 
                        gap: '10px', 
                        alignItems: 'flex-start', 
                        padding: '10px', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '6px',
                        marginBottom: '10px',
                        backgroundColor: '#f9fafb'
                      }}>
                        <div className="form-group" style={{ flex: 2, margin: 0, position: 'relative' }}>
                          <label style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Product *</label>
                          <input
                            type="text"
                            placeholder={
                              !(formData.fromType === 'PROJECT' ? formData.fromProjectId : formData.fromWarehouseId)
                                ? 'Select source location first'
                                : sourceStockLoading
                                ? 'Loading source stock...'
                                : sourceStock.length === 0
                                ? 'No items in stock'
                                : 'Type to search product...'
                            }
                            value={row.dropdownOpen ? (row.searchQuery ?? '') : (sourceStock.find(s => String(s.inventoryItemId) === String(row.inventoryItemId)) ? `${sourceStock.find(s => String(s.inventoryItemId) === String(row.inventoryItemId)).itemName} (${sourceStock.find(s => String(s.inventoryItemId) === String(row.inventoryItemId)).itemCode})` : '')}
                            onFocus={() => {
                              const newItems = [...items];
                              newItems[index].searchQuery = '';
                              newItems[index].dropdownOpen = true;
                              setItems(newItems);
                            }}
                            onChange={(e) => {
                              const newItems = [...items];
                              newItems[index].searchQuery = e.target.value;
                              setItems(newItems);
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                const newItems = [...items];
                                newItems[index].dropdownOpen = false;
                                setItems(newItems);
                              }, 200);
                            }}
                            required
                            disabled={!(formData.fromType === 'PROJECT' ? formData.fromProjectId : formData.fromWarehouseId) || sourceStockLoading || sourceStock.length === 0}
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px', cursor: 'pointer' }}
                          />
                          {row.dropdownOpen && (
                            <div className="custom-select-dropdown" style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 10,
                              background: 'var(--bg-card, #ffffff)',
                              border: '1px solid var(--border, #ddd)',
                              borderRadius: '6px',
                              boxShadow: 'var(--shadow, 0 4px 12px rgba(0,0,0,0.1))',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              marginTop: '4px'
                            }}>
                              {sourceStock
                                .filter(stock => {
                                  const query = (row.searchQuery ?? '').toLowerCase();
                                  return stock.itemName.toLowerCase().includes(query) || stock.itemCode.toLowerCase().includes(query);
                                })
                                .map(stock => {
                                  const isSelectedElsewhere = items.some((x, i) => i !== index && String(x.inventoryItemId) === String(stock.inventoryItemId));
                                  return (
                                    <div
                                      key={stock.inventoryItemId}
                                      className="custom-select-option"
                                      onMouseDown={(e) => {
                                        if (isSelectedElsewhere) {
                                          e.preventDefault();
                                          return;
                                        }
                                        e.preventDefault();
                                        handleRowItemChange(index, String(stock.inventoryItemId));
                                      }}
                                      style={{
                                        padding: '8px 12px',
                                        cursor: isSelectedElsewhere ? 'not-allowed' : 'pointer',
                                        fontSize: '13px',
                                        borderBottom: '1px solid var(--border, #f0f0f0)',
                                        backgroundColor: String(stock.inventoryItemId) === String(row.inventoryItemId) 
                                          ? 'var(--menu-active-bg, #e8f4ff)' 
                                          : 'transparent',
                                        color: isSelectedElsewhere 
                                          ? 'var(--text-secondary, #999)' 
                                          : (String(stock.inventoryItemId) === String(row.inventoryItemId) ? 'var(--menu-active-text, #0056b3)' : 'var(--text-primary, inherit)'),
                                        opacity: isSelectedElsewhere ? 0.6 : 1
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSelectedElsewhere && String(stock.inventoryItemId) !== String(row.inventoryItemId)) {
                                          e.target.style.backgroundColor = 'var(--border, #f5f5f5)';
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSelectedElsewhere && String(stock.inventoryItemId) !== String(row.inventoryItemId)) {
                                          e.target.style.backgroundColor = 'transparent';
                                        }
                                      }}
                                    >
                                      {stock.itemName} ({stock.itemCode}) {isSelectedElsewhere && '(Selected)'}
                                    </div>
                                  );
                                })
                              }
                              {sourceStock.filter(stock => {
                                const query = (row.searchQuery ?? '').toLowerCase();
                                return stock.itemName.toLowerCase().includes(query) || stock.itemCode.toLowerCase().includes(query);
                              }).length === 0 && (
                                <div style={{ padding: '8px 12px', color: 'var(--text-secondary, #999)', fontSize: '13px', textAlign: 'center' }}>
                                  No items found
                                </div>
                              )}
                            </div>
                          )}
                          {selectedStockItem && (
                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                              Avail: <strong>{selectedStockItem.quantity} {selectedStockItem.unitOfMeasurement}</strong>
                            </div>
                          )}
                        </div>

                        <div className="form-group" style={{ flex: 1, margin: 0 }}>
                          <label style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Qty *</label>
                          <input
                            type="number"
                            step="any"
                            min="0.001"
                            placeholder="Qty"
                            value={row.quantity}
                            onChange={e => handleRowQuantityChange(index, e.target.value)}
                            disabled={!row.inventoryItemId}
                            required
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                          />
                        </div>

                        <div className="form-group" style={{ flex: 1, margin: 0 }}>
                          <label style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Unit Cost</label>
                          <input
                            type="number"
                            step="any"
                            min="0"
                            placeholder="Cost"
                            value={unitPriceVal}
                            onChange={e => handleRowUnitPriceChange(index, e.target.value)}
                            disabled={!row.inventoryItemId}
                            required
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                          />
                        </div>

                        <div className="form-group" style={{ flex: 1.2, margin: 0 }}>
                          <label style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Total</label>
                          <input
                            type="text"
                            readOnly
                            value={formatCurrency(totalAmount)}
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', background: '#f3f4f6', fontSize: '13px', fontWeight: 'bold', color: '#10b981' }}
                          />
                        </div>

                        <div className="form-group" style={{ flex: 1.5, margin: 0 }}>
                          <label style={{ fontSize: '11px', textTransform: 'uppercase', color: '#6b7280' }}>Transfer Notes</label>
                          <input
                            type="text"
                            placeholder="Notes"
                            value={row.notes}
                            onChange={e => handleRowNotesChange(index, e.target.value)}
                            disabled={!row.inventoryItemId}
                            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}
                          />
                        </div>

                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveRow(index)}
                            style={{
                              alignSelf: 'center',
                              marginTop: '15px',
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              fontSize: '20px',
                              cursor: 'pointer',
                              padding: '0 5px'
                            }}
                            title="Remove Row"
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="form-group">
                  <label>Overall Remarks / General Notes</label>
                  <textarea 
                    value={formData.notes} 
                    onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="General reason for transfer..."
                    rows="2"
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', resize: 'vertical' }}
                  />
                </div>
              </div>
              
              <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="btn-secondary" onClick={() => setShowFormModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={items.some(x => !x.inventoryItemId)}>Initiate Transfer</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <DeleteConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmClass={confirmModal.confirmClass}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={handleConfirmAction}
        icon="❓"
      />
    </div>
  );
}
